import { ref } from "vue"
import {
  checkUser, chunkedUpload, findAvailableName, buildWikitext,
  sanitizeTitle, setCaptions, UploadWarningsError, payloadSize,
  type UploadPayload
} from "@/lib/commons-api"
import { linkFileToWikidataItem, type LinkResult } from "@/lib/wikidata-api"
import { saveResumeState, clearResumeState } from "@/lib/resume-storage"
import { deleteEncodedFile } from "@/lib/encoded-store"
import { UPLOAD_TAG } from "@/lib/config"
import { computeIdentityHash } from "@/lib/file-identity"
import type {
  UploadForm, UploadResult, ResumeState, LocalizedDescription, WikidataItem
} from "@/lib/types"

/**
 * Optional context for resuming an interrupted upload. Pass `resumeFrom`
 * (the persisted ResumeState from IndexedDB) when continuing a previously
 * stashed upload - we'll skip name resolution and reuse the existing filekey.
 *
 * `confirmWarnings` is set on the second pass after the user has reviewed and
 * accepted the warnings the server reported on the first attempt.
 *
 * `sourceFile` is the original on-disk File the user picked. It only means
 * anything for a passthrough, where `bytes` IS that same File handed
 * straight through. When provided, useUploader computes a head+tail+size
 * hash and persists it to the resume entry so a later resume can verify the
 * same file was re-picked.
 */
export interface UploadCallOptions {
  resumeFrom?: ResumeState | null
  confirmWarnings?: boolean
  sourceFile?: File | null
  /** OPFS key of the encoded output, when it was written to disk. */
  outputKey?: string
}

/**
 * Pending warnings surfaced to the caller after a finalize that returned
 * `result: "Warning"`. The caller presents these to the user and, on
 * confirmation, retries `upload` with `confirmWarnings: true`.
 */
export interface PendingWarnings {
  warnings: Record<string, unknown>
  filekey: string
  filename: string
}

/**
 * Plain copies of the post-upload fields for the resume entry. The form
 * arrives as a Vue reactive proxy, and IndexedDB cannot store a proxy: the
 * put would throw, saveResumeState would swallow it, and no resume entry
 * would exist at all. Defensive on shape, because this runs on the upload
 * path and a malformed row must not be able to throw here.
 */
function plainCaptions(captions: UploadForm["captions"] | undefined): LocalizedDescription[] {
  return (Array.isArray(captions) ? captions : [])
    .filter(c => !!c && typeof c.text === "string")
    .map(c => ({ lang: String(c.lang ?? ""), text: c.text }))
}

function plainWikidataItem(item: UploadForm["wikidataItem"]): WikidataItem | null {
  return item && typeof item.qid === "string"
    ? { qid: item.qid, label: String(item.label ?? ""), description: String(item.description ?? "") }
    : null
}

export function useUploader() {
  const phase = ref<"idle" | "checking" | "uploading" | "done" | "error" | "cancelled" | "warnings">("idle")
  /** Set when the file uploaded but its captions could not be saved. The
   *  upload itself still counts as a success. */
  const captionWarning = ref<string | null>(null)
  /** Set when the file uploaded but the Wikidata statement could not be
   *  written. Like captions, this never makes the upload itself a failure. */
  const wikidataWarning = ref<string | null>(null)
  /** The statement that was written, so the result screen can link to it.
   *  Null when no item was picked. */
  const wikidataLink = ref<LinkResult | null>(null)
  const progress = ref(0)
  const uploadedBytes = ref(0)
  const errorMsg = ref<string | null>(null)
  const lastResult = ref<UploadResult | null>(null)
  /** The most recent in-flight resume state, exposed so the caller can show
   *  a "Resume" button after an error within the same session. */
  const resumeState = ref<ResumeState | null>(null)
  /** Set when the server returns warnings on finalize. The App.vue layer
   *  uses this to render a confirmation dialog. */
  const pendingWarnings = ref<PendingWarnings | null>(null)

  let abortController: AbortController | null = null

  function reset() {
    phase.value = "idle"; progress.value = 0; uploadedBytes.value = 0
    errorMsg.value = null; lastResult.value = null; resumeState.value = null
    pendingWarnings.value = null
    captionWarning.value = null
    wikidataWarning.value = null
    wikidataLink.value = null
  }

  function cancel() {
    if (abortController) {
      abortController.abort()
      phase.value = "cancelled"
    }
  }

  async function upload(
    bytes: UploadPayload, form: UploadForm, extension: string,
    encodeMs: number, audioIncluded: boolean, passthrough: boolean,
    options?: UploadCallOptions
  ): Promise<UploadResult> {
    // Reset everything except pendingWarnings - caller may retry with
    // confirmWarnings:true and we want the next call to clear naturally.
    phase.value = "checking"
    progress.value = 0
    uploadedBytes.value = 0
    errorMsg.value = null
    lastResult.value = null
    if (!options?.confirmWarnings) pendingWarnings.value = null
    abortController = new AbortController()
    const signal = abortController.signal

    const resumeFrom = options?.resumeFrom ?? null
    const confirmWarnings = options?.confirmWarnings === true

    try {
      const auth = await checkUser()
      if (!auth.hasUpload) {
        throw new Error("Your account does not have upload rights on Commons.")
      }
      if (!auth.isAutoconfirmed) {
        throw new Error(
          "Your account is not autoconfirmed yet. Commons requires accounts " +
          "to be at least 4 days old and have at least 10 edits before they " +
          "can use upload tools. Please come back once your account is autoconfirmed."
        )
      }

      // When resuming, reuse the previously chosen filename. Otherwise, run
      // through availability resolution exactly as before.
      let finalFilename: string
      let wikitext: string
      if (resumeFrom) {
        finalFilename = resumeFrom.filename
        wikitext = resumeFrom.wikitext
      } else {
        const cleanTitle = sanitizeTitle(form.title)
        if (!cleanTitle) throw new Error("Title is empty after sanitization. Use a descriptive name.")
        finalFilename = await findAvailableName(cleanTitle, extension)
        wikitext = buildWikitext({ ...form, title: cleanTitle }, auth.name)
      }

      // Compute identity hash up-front when the source File is available.
      // For non-passthrough uploads we leave it undefined (the encoded bytes
      // are non-deterministic so a hash of the source file wouldn't help
      // verify the *upload* anyway).
      // Computed for EVERY upload, not only passthroughs. It identifies
      // the SOURCE the contributor picked, which is what a resume has to
      // match them against; it was previously skipped for re-encoded
      // uploads, leaving them with nothing to check but a size that
      // belonged to the encoded output.
      let identityHash: string | undefined = resumeFrom?.identityHash
      if (!identityHash && options?.sourceFile) {
        try {
          identityHash = await computeIdentityHash(options.sourceFile)
        } catch {
          // crypto.subtle unavailable or read error - degrade gracefully
          // (size check still applies).
        }
      }

      // Seed an in-memory resume state. Persist it after every successful chunk.
      const baseState: ResumeState = {
        id: finalFilename,
        filename: finalFilename,
        filekey: resumeFrom?.filekey ?? "",
        offset: resumeFrom?.offset ?? 0,
        totalBytes: payloadSize(bytes),
        fileSize: resumeFrom?.fileSize ?? payloadSize(bytes),
        fileName: resumeFrom?.fileName ?? finalFilename,
        sourceSize: resumeFrom?.sourceSize ?? options?.sourceFile?.size,
        sourceName: resumeFrom?.sourceName ?? options?.sourceFile?.name,
        outputKey: resumeFrom?.outputKey ?? options?.outputKey,
        createdAt: resumeFrom?.createdAt ?? new Date().toISOString(),
        wikitext,
        encodeMs,
        audioIncluded,
        passthrough,
        identityHash,
        captions: plainCaptions(form.captions),
        wikidataItem: plainWikidataItem(form.wikidataItem)
      }
      resumeState.value = baseState

      phase.value = "uploading"
      const result = await chunkedUpload({
        bytes, filename: finalFilename, csrfToken: auth.csrfToken, wikitext, signal,
        onProgress: (p) => { progress.value = p.ratio; uploadedBytes.value = p.uploadedBytes },
        resumeFrom: resumeFrom ? { filekey: resumeFrom.filekey, offset: resumeFrom.offset } : null,
        onChunkComplete: async (cp) => {
          const next: ResumeState = { ...baseState, filekey: cp.filekey, offset: cp.offset }
          resumeState.value = next
          await saveResumeState(next)
        },
        confirmWarnings,
        tag: UPLOAD_TAG || undefined
      })

      // Upload finalized - drop the resume entry and the encoded output on
      // disk; there is nothing left to recover and the file is only taking
      // up the contributor's storage until the next sweep.
      await clearResumeState(finalFilename)
      await deleteEncodedFile(baseState.outputKey)
      resumeState.value = null
      pendingWarnings.value = null

      // Captions are structured data on the file's MediaInfo entity, so
      // they are a separate Wikibase edit that can only run once the file
      // exists. Deliberately non-fatal: the upload has already succeeded
      // and the contributor can add captions on-wiki, so a failure here is
      // surfaced as a warning rather than turning a good upload into an
      // error the user cannot act on.
      captionWarning.value = null
      try {
        await setCaptions(result.filename, form.captions || [], auth.csrfToken)
      } catch (err) {
        captionWarning.value = err instanceof Error ? err.message : String(err)
        console.warn("[c2c] captions not saved:", captionWarning.value)
      }

      // Optional Wikidata link (P10 video / P51 audio). Same contract as
      // captions and for the same reason: the file exists on Commons by
      // now, so a Wikidata failure is reported and the upload still stands.
      // Wikidata needs its own CSRF token - the commonswiki one above is
      // not valid there - which linkFileToWikidataItem fetches for itself.
      wikidataWarning.value = null
      wikidataLink.value = null
      const wdItem = form.wikidataItem
      if (wdItem && wdItem.qid) {
        try {
          wikidataLink.value = await linkFileToWikidataItem({
            qid: wdItem.qid,
            filename: result.filename,
            mediaMode: form.mediaMode
          })
        } catch (err) {
          wikidataWarning.value = err instanceof Error ? err.message : String(err)
          console.warn("[c2c] Wikidata statement not added:", wikidataWarning.value)
        }
      }

      const enriched: UploadResult = { ...result, encodeMs, audioIncluded, passthrough }
      phase.value = "done"; progress.value = 1; lastResult.value = enriched
      return enriched
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        // User cancelled. Clear the resume entry - they don't want to recover.
        if (resumeState.value) {
          await clearResumeState(resumeState.value.id)
          resumeState.value = null
        }
        phase.value = "cancelled"; throw new Error("cancelled")
      }
      if (err instanceof UploadWarningsError) {
        // Surface to caller; bytes are already stashed under err.filekey, so
        // confirming-and-retrying will skip straight to finalize.
        const filename = resumeState.value?.filename || ""
        pendingWarnings.value = {
          warnings: err.warnings,
          filekey: err.filekey,
          filename
        }
        // Update resume state's filekey so retry skips the bytes.
        if (resumeState.value) {
          resumeState.value = {
            ...resumeState.value,
            filekey: err.filekey,
            offset: resumeState.value.totalBytes
          }
          await saveResumeState(resumeState.value)
        }
        phase.value = "warnings"
        throw err
      }
      phase.value = "error"; errorMsg.value = err instanceof Error ? err.message : String(err)
      // resumeState left intact - App.vue can read it and show a Resume button.
      throw err
    } finally {
      abortController = null
    }
  }

  return {
    phase, progress, uploadedBytes, errorMsg, lastResult, resumeState,
    pendingWarnings, captionWarning, wikidataWarning, wikidataLink,
    upload, reset, cancel
  }
}
